/**
 * useAIChatLoop — Supabase-native AI chat loop.
 * LLM simulates users; agentfactory handles all message writes, flags, and evals
 * via integration: { type: "simulation" }.
 */

import { useState, useCallback, useRef } from "react"
import { openaiService } from "../services"
import { AIChatThread, AILoopStartConfig, UseAIChatLoopResult } from "../types"
import { delay } from "../utils"
import { agents } from "@/lib/agents"
import { app } from "@/lib/app"
import { supabase } from "@/lib/data"
import { ensureSyntheticContact } from "./useSupabaseSimulation"

export function useAIChatLoop(): UseAIChatLoopResult {
  const [threads, setThreads] = useState<AIChatThread[]>([])
  const [running, setRunning] = useState(false)
  const abortRef = useRef(false)

  const updateThread = (threadId: string, updates: Partial<AIChatThread>) => {
    setThreads(prev => prev.map(t => t.id === threadId ? { ...t, ...updates } : t))
  }

  const addTurn = (threadId: string, role: "user" | "assistant", content: string) => {
    setThreads(prev => prev.map(t => {
      if (t.id !== threadId) return t
      return {
        ...t,
        turns: [...t.turns, { role, content, timestamp: Date.now() }],
        currentTurn: role === "assistant" ? t.currentTurn + 1 : t.currentTurn,
      }
    }))
  }

  const startLoop = useCallback(async (config: AILoopStartConfig) => {
    setRunning(true)
    abortRef.current = false

    const { conversations, systemPrompt, model, maxTurns, agentId, teamId } = config

    try {
      await openaiService.initialize(teamId)
    } catch {
      /* initialize is best-effort; later calls may still fail visibly */
    }

    const apiKeys = await app.fetchAPIkeys(teamId)

    const testAgent = await agents.loadAgent(agentId as any, teamId)
    if (!testAgent) {
      setRunning(false)
      throw new Error("Agent not found")
    }

    const newThreads: AIChatThread[] = conversations.map(conv => ({
      id: crypto.randomUUID(),
      // Keep a stable reference to the originating test-case row.
      testCaseId: conv.testCaseId || "",
      label: conv.label,
      turns: [],
      status: "active" as const,
      currentTurn: 0,
    }))

    setThreads(newThreads)

    const runThread = async (thread: AIChatThread, conv: typeof conversations[0]) => {
      let conversationHistory: Array<{ role: "user" | "assistant"; content: string }> = []
      let contactId: string | null = null
      let isEscalated = false

      try {
        contactId = await ensureSyntheticContact(null, teamId, conv.label)
        updateThread(thread.id, { contactId } as any)

        addTurn(thread.id, "user", conv.firstMessage)
        conversationHistory.push({ role: "user", content: conv.firstMessage })

        for (let turn = 0; turn < maxTurns; turn++) {
          if (abortRef.current || isEscalated) break

          const lastUserMessage = conversationHistory[conversationHistory.length - 1].content
          const MAX_AGENT_RETRIES = 3
          let botResponse = ""

          let turnEvalPromise: Promise<void> | undefined
          for (let retry = 0; retry < MAX_AGENT_RETRIES; retry++) {
            const p: AgentParameters = {
              debug: false,
              uid: contactId,
              team: teamId,
              input: { message: lastUserMessage },
              apiKeys,
              // agentfactory owns user msg save, agent msg save, flags, and evals
              integration: { type: "app", contact: contactId },
            }

            const agentInstance = await agents.loadAgent(agentId as any, teamId)
            if (!agentInstance) throw new Error("Agent not found")
            await agentInstance.execute(p)
            if (p.error) continue
            botResponse = p.output?.response || ""
            turnEvalPromise = p.evalPromise
            if (botResponse) break
          }

          if (!botResponse) break

          // Check if agent escalated (sets HITL flag in state)
          const { data: stateRow } = await supabase
            .from("states")
            .select("state")
            .eq("id", contactId)
            .single()
          if ((stateRow?.state as any)?.agent?.hitl?.active) {
            isEscalated = true
          }

          addTurn(thread.id, "assistant", botResponse)
          conversationHistory.push({ role: "assistant", content: botResponse })

          if (isEscalated) {
            updateThread(thread.id, { status: "escalated" })
            break
          }

          if (turn >= maxTurns - 1) {
            // Await evals on the last turn before marking complete
            if (turnEvalPromise) {
              updateThread(thread.id, { status: "evaluating" })
              await turnEvalPromise.catch(() => { })
            }
            break
          }

          // Await evals before proceeding to next turn so flags/items are written
          if (turnEvalPromise) await turnEvalPromise.catch(() => { })

          // LLM generates next user message
          try {
            const convSystemPrompt = conv.systemPrompt || systemPrompt
            const nextUserMessage = await openaiService.generateNextMessage(
              convSystemPrompt,
              conversationHistory,
              model
            )

            if (!nextUserMessage?.trim()) break

            // The next user message is written by the LLM loop (not agentfactory),
            // so we insert it directly. agentfactory will write the following
            // assistant reply and its own user-message save on the next turn —
            // but since integration.type === "simulation", it re-uses the uid to
            // fetch state and saves its own user msg. To avoid duplication on
            // subsequent turns we pass the user message via input only and let
            // agentfactory write it.
            addTurn(thread.id, "user", nextUserMessage)
            conversationHistory.push({ role: "user", content: nextUserMessage })
          } catch (llmErr) {
            updateThread(thread.id, {
              status: "error",
              error: `LLM error: ${llmErr instanceof Error ? llmErr.message : "Unknown"}`,
            })
            break
          }

          await delay(200)
        }

        setThreads(prev => prev.map(t =>
          t.id === thread.id && t.status === "active"
            ? { ...t, status: "completed" }
            : t
        ))
      } catch (err) {
        updateThread(thread.id, {
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        })
      }
    }

    await Promise.all(newThreads.map((thread, ci) => runThread(thread, conversations[ci])))

    setRunning(false)
  }, [])

  const stopLoop = useCallback(() => {
    abortRef.current = true
    setRunning(false)
  }, [])

  const clear = useCallback(() => setThreads([]), [])

  return {
    threads,
    running,
    config: { enabled: false, maxTurns: 5, model: "openai/gpt-4.1-mini", systemPrompt: "" },
    updateConfig: () => { },
    startLoop,
    stopLoop,
    clear,
  }
}
